import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowRight, Gift, Users, Zap } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const GiftingFeature = () => {
  const features = [
    {
      icon: <Gift className="w-6 h-6" />,
      title: "Effortless Gifting",
      description: "Send personalized gifts with just a few clicks"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Team Celebrations",
      description: "Celebrate milestones and achievements together",
      comingSoon: true
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Instant Delivery",
      description: "Digital gifts delivered seamlessly"
    },
    {
      icon: <Gift className="w-6 h-6" />,
      title: "Cancellable Rewards",
      description: "Flexible reward management with easy cancellation options",
      comingSoon: true
    },
    {
      icon: <Gift className="w-6 h-6" />,
      title: "Extensive Catalog",
      description: "Choose from over 2000+ reward options and gift cards"
    }
  ];

  return (
    <section className="py-24 lg:py-32 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            <div className="space-y-6">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground leading-tight">
                Make Gifting{' '}
                <span className="text-gradient bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  Effortless
                </span>
              </h2>
              
              <p className="text-xl text-muted-foreground leading-relaxed">
                Send gifts to employees or your customers seamlessly - all in one place.
              </p>
            </div>

            {/* Features list */}
            <div className="space-y-6">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex items-start gap-4"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center text-primary">
                    {feature.icon}
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold text-foreground">
                        {feature.title}
                      </h3>
                      {feature.comingSoon && (
                        <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700 hover:bg-blue-100">
                          Coming Soon
                        </Badge>
                      )}
                    </div>
                    <p className="text-muted-foreground">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

          </motion.div>

          {/* Visual */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            {/* Gifting rewards showcase */}
            <div className="flex justify-center">
              <motion.img
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3 }}
                src="/gifting-rewards.webp" 
                alt="Employee gifting platform showing diverse reward options including gift cards, experiences, and recognition"
                className="w-full max-w-lg h-auto object-contain"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default GiftingFeature;